/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect, useState } from 'react';
import SEOLocaleList from '@apps/components/SEOLocaleList';
import SideAds from '@apps/components/SideAds';
import { BASE_URL } from '@apps/config';
import getNewsListing from '@apps/config/API/listing.config';
import { getNewCarById } from '@apps/config/API/new-car.config';
import NewsListingScreen from '@apps/screens/NewsListing';
import { localeConversions } from '@apps/utils/locale-conversion';
import { Breadcrumb } from '@stack/ui/components';
import { BreadCrumbOptions } from '@stack/ui/components/Breadcrumb/props';
import { NextPage, NextPageContext } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import useTranslation from 'next-translate/useTranslation';

/**
 * ===========================
 * MAIN
 * ===========================
 */
const Page: NextPage = (props) => {
  // ======================= HOOKS
  const { settings, detail, webView } = props;
  const router = useRouter();
  const { lang, t } = useTranslation('common');
  const newLang = localeConversions(lang);

  // ======================= STATE
  const carBrand = settings?.car_brand_featured;
  const pageSize = 40;
  const [data, setData] = useState<any>();
  const [loading, setloading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [cars, setCars] = useState();

  // ======================= API
  const fetchData = async (pageNumber: number) => {
    try {
      setloading(true);
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth' as any,
        });
      }, 0);

      const res = await getNewsListing({
        pageSize,
        page: pageNumber,
        locale: newLang,
        latest: true,
      });
      if (res.data.length < 1) {
        router.replace(webView ? '/webview/404' : '/404');
        return;
      }

      const newEvCar = await getNewCarById({ id: 4 });
      const newCars = newEvCar?.data?.find((car: any) => {
        return car.new_car_feature_id === 4;
      });

      setCars(newCars);
      setData(res);
      setloading(false);
    } catch (err) {
      console.log('err', err);
    }
  };

  useEffect(() => {
    const { page } = router.query;
    setCurrentPage(parseInt(page as string) || 1);
    fetchData(parseInt(page as string) || 1);
  }, [router?.asPath, router?.locale]);

  const news = data;

  // ======================= VARIABLES
  const currentPath = 'latest';

  const breadcrumbOpt: BreadCrumbOptions[] = [
    {
      title: t('news'),
      url: webView ? '/webview' : '/',
    },
    {
      title: t('latest'),
    },
  ];

  // ======================= VIEWS

  // ======================= SEO
  const seoTitle = `Malaysia Latest News - Latest Models & Upcoming Launches | Carz Automedia Malaysia`;
  const seoDesc = detail?.data?.post_short_description[newLang];
  const seoImage = detail?.data?.post_image[newLang];
  const currentUrl =
    lang === 'en'
      ? `${BASE_URL}${router?.asPath}`
      : `${BASE_URL}/${lang}${router?.asPath}`;
  const cleanedPath = currentUrl.split('?')[0].toLowerCase();

  return (
    <>
      <Head>
        {seoImage ? (
          <>
            <meta name="twitter:title" content={seoTitle} />
            <meta name="twitter:description" content={seoDesc} />
            <meta name="twitter:image" content={seoImage} />
            <meta name="twitter:image:type" content="image/jpeg" />
            <meta name="twitter:url" content={cleanedPath} />
          </>
        ) : (
          <meta name="twitter:url" content={cleanedPath} />
        )}
      </Head>
      <NextSeo
        title={seoTitle}
        titleTemplate={seoTitle}
        description={seoDesc}
        openGraph={{
          ...(seoImage
            ? {
                url: cleanedPath,
                images: [
                  {
                    url: seoImage,
                    alt: seoTitle,
                    type: 'image/jpeg',
                  },
                ],
              }
            : { url: currentUrl }),
        }}
        canonical={cleanedPath}
      />
      <SEOLocaleList />
      <SideAds
        adsKeyLeft={'news_listing_vertical_left'}
        adsKeyRight={'news_listing_vertical_right'}
      />
      <div>
        <Breadcrumb options={breadcrumbOpt} />

        <h1 className="text-3xl">{t('latest')}</h1>
        <NewsListingScreen
          categorySlug={currentPath}
          data={news?.data}
          currentPage={currentPage}
          totalCount={news?.meta?.total ?? 0}
          pageSize={pageSize}
          carBrand={carBrand}
          newEvCar={cars}
          webView={webView}
          loading={loading}
          href={`/${currentPath}`}
        />
      </div>
    </>
  );
};

export async function getServerSideProps(ctx: NextPageContext) {
  const locale = ctx?.locale ?? 'en';
  const newLocale = localeConversions(locale);

  const data = await getNewsListing({
    pageSize: 40,
    page: 1,
    locale: newLocale,
    latest: true,
  });
  const hasData = data?.data?.length > 0;
  if (!hasData) {
    return { notFound: true };
  }

  return { props: {} };
}

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default Page;
